import { memo, useCallback, useEffect, useRef } from 'react';

import { SidebarResizer } from '@air/component-sidebar';

import { useWorkspaceSidebarOffsets } from '~/components/v2/components/workspace/workspace-sidebar/useWorkspaceSidebarOffsets';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { SIDEBAR_SIZE } from '~/constants/localStorageKeys';
import { WORKSPACE_CANVAS_HORIZONTAL_PADDING, WORKSPACE_MIN_WIDTH } from '~/constants/panelWidths';
import { CURRENT_WORKSPACE_NAV_WIDTH } from '~/constants/sideNav';

export const WorkspaceSidebarResizer = memo(() => {
  const open = useWorkspaceSidebarStore((state) => state.open);
  const toggleOpen = useWorkspaceSidebarStore((state) => state.toggleOpen);
  const setIsResizing = useWorkspaceSidebarStore((state) => state.setIsResizing);
  const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
  const rightPanelOpen = useWorkspaceRightPanelOpen();
  const rightPanelWidth = useWorkspaceRightPanelStore((state) => state.width);
  const setRightPanelWidth = useWorkspaceRightPanelStore((state) => state.setWidth);
  const { setToastXOffset, setActionBarXOffset } = useWorkspaceSidebarOffsets({
    isWorkspaceNavPushMode: isWorkspaceNavPushMode || !open,
    open,
  });
  const rightPanelResizeRafRef = useRef<number | null>(null);
  const pendingRightPanelWidthRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (rightPanelResizeRafRef.current !== null) {
        window.cancelAnimationFrame(rightPanelResizeRafRef.current);
      }
    };
  }, []);

  const handleResizeStart = useCallback(() => setIsResizing(true), [setIsResizing]);
  const handleResizeEnd = useCallback(() => setIsResizing(false), [setIsResizing]);
  const handleResize = useCallback(
    (width: number) => {
      setToastXOffset(width);
      setActionBarXOffset(width);

      if (!rightPanelOpen) {
        return;
      }

      const availableWidth = window.innerWidth - width - WORKSPACE_MIN_WIDTH - WORKSPACE_CANVAS_HORIZONTAL_PADDING;

      if (availableWidth < rightPanelWidth) {
        pendingRightPanelWidthRef.current = Math.max(0, availableWidth);
        if (rightPanelResizeRafRef.current === null) {
          rightPanelResizeRafRef.current = window.requestAnimationFrame(() => {
            if (pendingRightPanelWidthRef.current !== null) {
              setRightPanelWidth(pendingRightPanelWidthRef.current);
            }
            rightPanelResizeRafRef.current = null;
          });
        }
      }
    },
    [rightPanelOpen, rightPanelWidth, setActionBarXOffset, setRightPanelWidth, setToastXOffset],
  );

  if (!open || isWorkspaceNavPushMode) {
    return null;
  }

  return (
    <SidebarResizer
      className="top-0 right-auto bottom-0 left-0 h-auto"
      localStorageKey={SIDEBAR_SIZE}
      onClose={toggleOpen}
      onResize={handleResize}
      onResizeEnd={handleResizeEnd}
      onResizeStart={handleResizeStart}
      size={CURRENT_WORKSPACE_NAV_WIDTH}
    />
  );
});

WorkspaceSidebarResizer.displayName = 'WorkspaceSidebarResizer';
